import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { StaticSettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { BannerNotification, Text, TextLink } from '@frontend/design-system';
import { useJustifiOverrideStyles } from '../../hooks/use-justifi-override-styles';
import { useLoadJustifi } from '../../hooks/use-load-justifi';
import {
  JustifiPaymentProvisioningComponentProps,
  JustifiPaymentProvisioningSubmitEvent,
  JustifiPaymentProvisioningErrorEvent,
  JustifyPaymentProvisioningNextStepEvent,
} from '../../types';
import { styles, overrideStyles, inlineStyles } from './justifi-payment-provisioning.styles';

export type JustifiPaymentProvisioningProps = {
  justifiProps: JustifiPaymentProvisioningComponentProps;
  className?: string;
  onSubmitted?: (event: JustifiPaymentProvisioningSubmitEvent) => void;
  onError?: (error: JustifiPaymentProvisioningErrorEvent) => void;

  /**
   * Decide if an error is, in fact, an error.
   * For example, the "already been submitted" error will get a custom pending state.
   *
   * @returns `true` if error logic should be called, `false` if not.
   */
  customErrorHandler?: (error: JustifiPaymentProvisioningErrorEvent) => boolean;
  showErrorMessages?: boolean;
  showSuccessMessage?: boolean;
  successMessage?: string;
};

export function JustifiPaymentProvisioning({
  onError,
  onSubmitted,
  customErrorHandler: customErrorHandlerControl,
  justifiProps,
  className,
  showErrorMessages,
  showSuccessMessage = true,
  successMessage,
}: JustifiPaymentProvisioningProps) {
  const formRef = useRef<HTMLJustifiPaymentProvisioningCoreElement>(null);
  const { t } = useTranslation('payments');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const navigate = (to: StaticSettingsRoutes) => settingsNavigate({ to });
  const [errorMessage, setErrorMessage] = useState<{ message: string; step: string } | null>(null);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState<JustifiPaymentProvisioningSubmitEvent['detail'] | null>(null);

  const { businessId, authToken, formTitle = 'Business Info', allowOptionalFields } = justifiProps;

  const {
    moduleStatuses: {
      onboarding: { isLoaded: justifiIsLoaded, isSuccess: justifiLoadedSuccess, hasErrorLoading },
    },
  } = useLoadJustifi(['onboarding']);

  const customErrorHandler = useMemo(() => {
    return (
      customErrorHandlerControl ||
      ((error: JustifiPaymentProvisioningErrorEvent) => {
        if (error.detail.message.includes('already been submitted')) {
          setIsSubmitted(true);
          return false;
        }

        return true;
      })
    );
  }, [customErrorHandlerControl]);

  // Handle the submit event
  const handleSubmit = useCallback(
    (event: JustifiPaymentProvisioningSubmitEvent) => {
      /**
       * Handle submit fires on every step.
       */

      const detail = event.detail;

      const type = detail.response?.submittedData?.type || detail.response?.type;
      const previousType = submittedData?.response?.submittedData?.type || submittedData?.response?.type;

      if (detail.response?.data) {
        /**
         * response key seems to only be present on final submit
         */
        setErrorMessage(null);
        setIsSubmitted(true);
        onSubmitted?.(event);
      } else {
        /**
         * If a submission error occurs, the error event will fire first, and then the submit event.
         * So we must check if the step has changed before clearing.
         * Otherwise we'll clear a newly set error.
         */
        if (type !== previousType) {
          setErrorMessage(null);
        }
      }

      /**
       * Set submitted data for current step, so on next step, we can compare previous step data.
       */
      setSubmittedData(event.detail);
    },
    [submittedData?.response?.submittedData?.type, submittedData?.response?.type, onSubmitted]
  );

  const handleError = useCallback(
    (event: JustifiPaymentProvisioningErrorEvent) => {
      const isError = customErrorHandler(event);
      if (!isError) {
        return;
      }

      // Handle the error event
      if (showErrorMessages) {
        setErrorMessage({
          message: event.detail.message,
          step: currentStep || '',
        });
      }
      onError?.(event);
    },
    [currentStep, showErrorMessages, customErrorHandler, onError]
  );

  const handleNextStep = useCallback(
    (e: Event) => {
      const event = e as JustifyPaymentProvisioningNextStepEvent;
      setCurrentStep(event.detail.formStep);

      if (errorMessage?.message && !errorMessage.step) {
        /**
         * On first step + first error message, the step won't be set yet.
         * So set it.
         */
        setErrorMessage({
          message: errorMessage.message,
          step: event.detail.formStep,
        });
      } else if (errorMessage && errorMessage.step !== event.detail.formStep) {
        /**
         * We're on a new step. Error message of previous step should go away.
         */
        setErrorMessage(null);
      } else if (errorMessage?.step === event.detail.formStep && typeof event.detail.response !== 'string') {
        /**
         * When response is string, it's an error message.
         * When it's an object, the response was successful.
         * Clear Error message.
         */
        setErrorMessage(null);
      }
    },
    [errorMessage]
  );

  const safeDependencies = useMemo(() => {
    return [businessId, authToken, isSubmitted, justifiIsLoaded];
  }, [businessId, authToken, isSubmitted, justifiIsLoaded]);

  useJustifiOverrideStyles({
    selector: () => formRef.current?.shadowRoot?.querySelector('justifi-payment-provisioning-core'),
    overrideStyles,
    dependencies: safeDependencies,
  });

  /**
   * Re-add submit listener when handleSubit changes.
   */
  useEffect(() => {
    const form = formRef.current;
    if (!form || !justifiLoadedSuccess) {
      return;
    }

    form.addEventListener('submit-event', handleSubmit);

    return () => {
      form.removeEventListener('submit-event', handleSubmit);
    };
  }, [businessId, authToken, isSubmitted, justifiLoadedSuccess, handleSubmit]);

  /**
   * Re-add error listener when handleError changes, so callback can have
   * latest state.
   */
  useEffect(() => {
    const form = formRef.current;
    if (!form || !justifiLoadedSuccess) {
      return;
    }

    form.addEventListener('error-event', handleError);

    return () => {
      form.removeEventListener('error-event', handleError);
    };
  }, [businessId, authToken, isSubmitted, justifiLoadedSuccess, handleError]);

  /**
   * Re-add next step listener when handleNextStep changes, so callback can have
   * latest state.
   */
  useEffect(() => {
    const form = formRef.current;
    if (!form || !justifiLoadedSuccess) {
      return;
    }

    form.addEventListener('complete-form-step-event', handleNextStep);

    return () => {
      form.removeEventListener('complete-form-step-event', handleNextStep);
    };
  }, [businessId, authToken, isSubmitted, justifiLoadedSuccess, handleNextStep]);

  if (!businessId || !authToken) {
    return <div>{t('Loading business information . . .')}</div>;
  }

  if (hasErrorLoading) {
    return <div>{t('There was an error loading the payment processor. Please try again later.')}</div>;
  } else if (!justifiIsLoaded) {
    /**
     * No loader for actual component. Just nothing until it loads.
     */
    return null;
  }

  return (
    <div className={`justifi-payment-provisioning ${className}`} key={isSubmitted ? 'submitted' : 'not-submitted'}>
      <style>
        {/* These styles are done the way justifi wants */}
        {inlineStyles}
      </style>

      {showSuccessMessage && isSubmitted && (
        <BannerNotification
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
          status='success'
          message={
            successMessage ?? (
              <Trans t={t}>
                <Text>
                  Your form has been submitted and your account is being underwritten. Please check your{' '}
                  <TextLink onClick={() => navigate(`/payments/account-info`)}>Account Info & Status</TextLink> in 24
                  hours for updates.
                </Text>
              </Trans>
            )
          }
        />
      )}

      <div className='form-wrapper' css={styles.formWrapper}>
        <justifi-payment-provisioning
          business-id={businessId}
          auth-token={authToken}
          form-title={formTitle}
          allow-optional-fields={allowOptionalFields}
          ref={formRef}
        />

        {isSubmitted && <div className='form-wrapper__pending-overlay' css={styles.pendingOverlay}></div>}
      </div>

      {showErrorMessages && !!errorMessage?.message && (
        <BannerNotification
          css={css`
            margin-top: ${theme.spacing(2)};
          `}
          status='error'
          message={errorMessage.message}
        />
      )}
    </div>
  );
}
